import { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { Pagination } from 'react-bootstrap';
import { Box, Typography, Paper, Tooltip } from '@mui/material';
import FeatherIcon from 'feather-icons-react';
import { Spinner } from 'reactstrap';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import { InspectionTrackerFormModal } from './InspectionTrackerFormModal';
import { renewInspectionRecord } from './FormikHelpers/Api';
import './inspection-tracker.scss';

const LS_KEY = 'it_table_col_widths';
const FACTORY_WIDTHS = {
  expand: 44,
  property: 240,
  dueDate: 130,
  inspectionType: 150,
  frequency: 130,
  itemCount: 100,
  overdue: 120,
  attachments: 120,
  updated: 140,
  actions: 130,
};

const loadSavedWidths = () => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    return raw ? { ...FACTORY_WIDTHS, ...JSON.parse(raw) } : { ...FACTORY_WIDTHS };
  } catch { return { ...FACTORY_WIDTHS }; }
};

const saveWidths = (widths) => {
  try { localStorage.setItem(LS_KEY, JSON.stringify(widths)); } catch { }
};

const fmtDate = (d) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// The due date is always the NEXT due date: past it = overdue, whether or not
// an earlier cycle was completed or skipped. "Done" / "Skipped" mean the last
// cycle was closed and the next due date is clear of the due-soon band. The
// server sends `status`; the arithmetic below is only a fallback for rows
// without it (mirrors the server rule).
const getDueDateStatus = (item) => {
  if (!item) return 'none';
  if (item.status === 'skipped') return 'skipped';
  if (item.status === 'overdue') return 'overdue';
  if (item.status === 'due_soon') return 'soon';
  if (item.status === 'done') return 'done';
  if (item.status === 'scheduled') return 'ok';
  if (!item.dueDate) return 'none';
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(item.dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due - now) / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return 'overdue';
  const dueSoonBand = Math.max(7, Math.min(90, Math.floor((item.frequencyDays || 365) / 2)));
  if (diffDays <= dueSoonBand) return 'soon';
  if (item.completed?.outcome === 'skipped') return 'skipped';
  return item.completed?.completedDate ? 'done' : 'ok';
};

const DUE_STYLES = {
  done: { background: '#F0FDF4', color: '#1A6B4A', border: '1px solid #A7F3D0' },
  overdue: { background: '#FEF2F2', color: '#DC2626', border: '1px solid #FECDD3' },
  soon: { background: '#FFFBEB', color: '#B45309', border: '1px solid #FDE68A' },
  skipped: { background: '#FEFCE8', color: '#A16207', border: '1px solid #FDE68A' },
  ok: { background: '#F0FDF4', color: '#0D9488', border: '1px solid #A7F3D0' },
  none: { background: '#F7FAF8', color: '#6B8F7C', border: '1px solid #D4E8DC' },
};
const DUE_ICONS = { done: 'check-circle', overdue: 'alert-circle', soon: 'clock', skipped: 'skip-forward', ok: 'calendar', none: 'minus' };

// ── Merged inspection row: Due Date | Type badge | Frequency badge in one line ──
const InspectionDetailRow = ({ item }) => {
  const status = getDueDateStatus(item);
  const s = DUE_STYLES[status];

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'nowrap' }}>
      {/* Due Date pill */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 6, padding: '2px 7px', fontSize: '0.70rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, ...s }}>
        <FeatherIcon icon={DUE_ICONS[status]} size={9} color={s.color} />
        {item.dueDate ? fmtDate(item.dueDate) : '—'}
      </span>

      {/* Type badge */}
      {item.inspectionTypeName && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 6, padding: '2px 7px', fontSize: '0.70rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
          <FeatherIcon icon="tag" size={9} color="#1D4ED8" />
          {item.inspectionTypeName}
        </span>
      )}

      {/* Frequency badge — reads frequencyName from API */}
      {item.frequencyName && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 6, padding: '2px 7px', fontSize: '0.70rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0, background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE' }}>
          <FeatherIcon icon="repeat" size={9} color="#4338CA" />
          {item.frequencyName}
        </span>
      )}
    </div>
  );
};

// Shows first 3 inspection rows, remaining in a hover tooltip
const InspectionDetailsCell = ({ row }) => {
  const sorted = useMemo(() =>
    [...(row.inspections || [])].sort((a, b) => {
      if (!a.dueDate) return 1;
      if (!b.dueDate) return -1;
      return new Date(a.dueDate) - new Date(b.dueDate);
    }), [row.inspections]);

  if (!sorted.length) return <span className="it-cell-muted">—</span>;

  const visible = sorted.slice(0, 3);
  const hidden = sorted.slice(3);

  const tooltipContent = hidden.length > 0 ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, padding: '4px 2px' }}>
      {hidden.map((item, i) => <InspectionDetailRow key={i} item={item} />)}
    </div>
  ) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      {visible.map((item, i) => <InspectionDetailRow key={i} item={item} />)}
      {hidden.length > 0 && (
        <Tooltip
          title={tooltipContent}
          arrow
          placement="right"
          componentsProps={{
            tooltip: {
              sx: {
                bgcolor: '#FFFFFF',
                border: '1px solid #D4E8DC',
                borderRadius: '10px',
                boxShadow: '0 8px 30px rgba(19,78,55,0.14)',
                p: '10px 14px',
                maxWidth: 520,
                '& .MuiTooltip-arrow': { color: '#FFFFFF', '&::before': { border: '1px solid #D4E8DC' } },
              },
            },
          }}
        >
          <span style={{ fontSize: '0.68rem', color: '#1A6B4A', fontWeight: 700, cursor: 'pointer', background: '#E8F5EE', border: '1px solid #A7F3D0', borderRadius: 5, padding: '1px 7px', display: 'inline-flex', alignItems: 'center', gap: 3, userSelect: 'none', alignSelf: 'flex-start' }}>
            <FeatherIcon icon="more-horizontal" size={10} color="#1A6B4A" />
            +{hidden.length} more
          </span>
        </Tooltip>
      )}
    </div>
  );
};

const DueDateLegendTooltip = () => {
  const legendContent = (
    <div style={{ padding: '4px 2px', minWidth: 170 }}>
      <div style={{ fontSize: '0.68rem', fontWeight: 700, color: '#6B8F7C', letterSpacing: '0.8px', textTransform: 'uppercase', marginBottom: 10 }}>Due Date Legend</div>
      {[
        { color: '#1A6B4A', bg: '#F0FDF4', border: '#A7F3D0', icon: 'check-circle', label: 'Done (next due shown)' },
        { color: '#0D9488', bg: '#F0FDFA', border: '#99F6E4', icon: 'calendar', label: 'Scheduled' },
        { color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', icon: 'clock', label: 'Due soon (last 3 months, or half of a shorter cycle)' },
        { color: '#A16207', bg: '#FEFCE8', border: '#FDE68A', icon: 'skip-forward', label: 'Skipped (with a note)' },
        { color: '#DC2626', bg: '#FEF2F2', border: '#FECDD3', icon: 'alert-circle', label: 'Overdue' },
      ].map(({ color, bg, border, icon, label }) => (
        <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 7 }}>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: bg, border: `1px solid ${border}`, borderRadius: 6, padding: '3px 10px', fontSize: '0.72rem', fontWeight: 700, color, whiteSpace: 'nowrap' }}>
            <FeatherIcon icon={icon} size={11} color={color} />
            {label}
          </span>
        </div>
      ))}
    </div>
  );

  return (
    <Tooltip title={legendContent} arrow placement="bottom-start" componentsProps={{ tooltip: { sx: { bgcolor: '#FFFFFF', border: '1px solid #D4E8DC', borderRadius: '12px', boxShadow: '0 12px 40px rgba(19,78,55,0.18)', p: '12px 16px', maxWidth: 300, '& .MuiTooltip-arrow': { color: '#FFFFFF', '&::before': { border: '1px solid #D4E8DC' } } } } }}>
      <button type="button"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'rgba(167,243,208,0.10)', border: '1px solid rgba(167,243,208,0.22)', borderRadius: 7, padding: '4px 10px', cursor: 'pointer', color: 'rgba(167,243,208,0.75)', fontSize: '0.72rem', fontWeight: 600, transition: 'all 0.15s ease' }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(167,243,208,0.18)'; e.currentTarget.style.color = '#A7F3D0'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(167,243,208,0.10)'; e.currentTarget.style.color = 'rgba(167,243,208,0.75)'; }}
      >
        <FeatherIcon icon="info" size={12} />Color Guide
      </button>
    </Tooltip>
  );
};

const ResizeHandle = ({ onResize }) => {
  const dragging = useRef(false);
  const startX = useRef(0);
  const onDown = (e) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX;
    const onMove = (ev) => { if (dragging.current) { onResize(ev.clientX - startX.current); startX.current = ev.clientX; } };
    const onUp = () => { dragging.current = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  };
  return <div className="it-resize-handle" onMouseDown={onDown}><div className="it-rhandle" /></div>;
};

const getOverdueCount = (row) => row.overdueCount || 0;
const getItemCount = (row) => row.inspections?.length || 0;
const getTotalAttachments = (row) => (row.inspections || []).reduce((s, i) => s + (i.completed?.attachments?.length || 0), 0);
const getCompletedCount = (row) => (row.inspections || []).filter((i) => getDueDateStatus(i) === 'done').length;
const getSkippedCount = (row) => (row.inspections || []).filter((i) => getDueDateStatus(i) === 'skipped').length;
const getReminderCount = (row) => (row.inspections || []).filter((i) => i.reminderDays).length;

const cellVal = (row, key, propertyContext) => {
  switch (key) {
    case 'property':
      return (
        <span>
          <div style={{ fontWeight: 700, fontSize: '0.82rem', color: '#1A6B4A' }}>{propertyContext.name || '—'}</div>
          <div style={{ fontSize: '0.72rem', color: '#6B8F7C' }}>{row.property?.buildingName || '—'}</div>
          {propertyContext.type && <div style={{ fontSize: '0.7rem', color: '#6B8F7C', marginTop: 1 }}>{propertyContext.type}</div>}
        </span>
      );
    case 'dueDate': {
      const sorted = [...(row.inspections || [])].sort((a, b) => {
        if (!a.dueDate) return 1;
        if (!b.dueDate) return -1;
        return new Date(a.dueDate) - new Date(b.dueDate);
      });
      if (!sorted.length) return <span className="it-cell-muted">—</span>;
      const visible = sorted.slice(0, 3);
      const hidden = sorted.slice(3);
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {visible.map((item, i) => {
            const status = getDueDateStatus(item);
            const s = DUE_STYLES[status];
            return (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 6, padding: '2px 7px', fontSize: '0.70rem', fontWeight: 600, whiteSpace: 'nowrap', ...s }} title={status === 'done' ? 'Completed; next due on this date' : status === 'skipped' ? 'Skipped this cycle, with a note; next due on this date' : status === 'overdue' ? 'Overdue' : status === 'soon' ? 'Due soon' : ''}>
                <FeatherIcon icon={DUE_ICONS[status]} size={9} color={s.color} />
                {item.dueDate ? fmtDate(item.dueDate) : '—'}
              </span>
            );
          })}
          {hidden.length > 0 && (
            <span style={{ fontSize: '0.68rem', color: '#1A6B4A', fontWeight: 700, cursor: 'pointer', background: '#E8F5EE', border: '1px solid #A7F3D0', borderRadius: 5, padding: '1px 7px', display: 'inline-flex', alignItems: 'center', gap: 3, alignSelf: 'flex-start' }}>
              <FeatherIcon icon="more-horizontal" size={10} color="#1A6B4A" />
              +{hidden.length} more
            </span>
          )}
        </div>
      );
    }

    case 'inspectionType': {
      const types = [...new Set((row.inspections || []).map((i) => i.inspectionTypeName).filter(Boolean))];
      if (!types.length) return <span className="it-cell-muted">—</span>;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {types.slice(0, 3).map((t, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 6, padding: '2px 7px', fontSize: '0.70rem', fontWeight: 600, whiteSpace: 'nowrap', background: '#EFF6FF', color: '#1D4ED8', border: '1px solid #BFDBFE' }}>
              <FeatherIcon icon="tag" size={9} color="#1D4ED8" />
              {t}
            </span>
          ))}
          {types.length > 3 && (
            <span style={{ fontSize: '0.68rem', color: '#1D4ED8', fontWeight: 700, background: '#EFF6FF', border: '1px solid #BFDBFE', borderRadius: 5, padding: '1px 7px', display: 'inline-flex', alignItems: 'center', gap: 3, alignSelf: 'flex-start' }}>
              <FeatherIcon icon="more-horizontal" size={10} color="#1D4ED8" />
              +{types.length - 3} more
            </span>
          )}
        </div>
      );
    }

    case 'frequency': {
      const freqs = [...new Set((row.inspections || []).map((i) => i.frequencyName).filter(Boolean))];
      if (!freqs.length) return <span className="it-cell-muted">—</span>;
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {freqs.slice(0, 3).map((f, i) => (
            <span key={i} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, borderRadius: 6, padding: '2px 7px', fontSize: '0.70rem', fontWeight: 600, whiteSpace: 'nowrap', background: '#EEF2FF', color: '#4338CA', border: '1px solid #C7D2FE' }}>
              <FeatherIcon icon="repeat" size={9} color="#4338CA" />
              {f}
            </span>
          ))}
          {freqs.length > 3 && (
            <span style={{ fontSize: '0.68rem', color: '#4338CA', fontWeight: 700, background: '#EEF2FF', border: '1px solid #C7D2FE', borderRadius: 5, padding: '1px 7px', display: 'inline-flex', alignItems: 'center', gap: 3, alignSelf: 'flex-start' }}>
              <FeatherIcon icon="more-horizontal" size={10} color="#4338CA" />
              +{freqs.length - 3} more
            </span>
          )}
        </div>
      );
    }
    case 'inspectionDetails': return <InspectionDetailsCell row={row} />;
    case 'itemCount': {
      const total = getItemCount(row);
      const done = getCompletedCount(row);
      const skipped = getSkippedCount(row);
      const reminders = getReminderCount(row);
      return (
        <span className="it-cell-number" title={`${done} of ${total} completed, ${skipped} skipped, ${reminders} with reminders`}>
          {total}
          {done > 0 && <span style={{ fontSize: '0.68rem', color: '#1A6B4A', marginLeft: 4, fontWeight: 600 }}>({done}✓)</span>}
          {reminders > 0 && <span style={{ fontSize: '0.68rem', color: '#D97706', marginLeft: 4, fontWeight: 600 }}>🔔</span>}
        </span>
      );
    }
    case 'overdue': {
      const c = getOverdueCount(row);
      return c > 0
        ? <span className="it-badge it-badge--overdue">{c} Overdue</span>
        : <span className="it-badge it-badge--ok">0 Overdue</span>;
    }
    case 'attachments': {
      const n = getTotalAttachments(row);
      return n > 0
        ? <span className="it-badge it-badge--files"><FeatherIcon icon="paperclip" size={10} /> {n} file{n !== 1 ? 's' : ''}</span>
        : <span className="it-cell-muted">—</span>;
    }
    case 'updated':
      return row.updatedAt
        ? <span className="it-cell-date">{fmtDate(row.updatedAt)}</span>
        : <span className="it-cell-muted">—</span>;
    default:
      return <span className="it-cell-text">{row[key] ?? '—'}</span>;
  }
};

const InspectionTrackerListTable = ({
  items = [],
  properties = [],
  filters = {},
  onDelete,
  onSaveRow,
  onRefresh,
  userRole,
  userLead = false,
  typeOptions = [],
  frequencyOptions = [],
  scrollThreshold = 992,
}) => {
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [currentPage, setCurrentPage] = useState(1);
  const [colWidths, setColWidths] = useState(loadSavedWidths);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [isNewModal, setIsNewModal] = useState(false);
  const [renewing, setRenewing] = useState(false);
  const PER_PAGE = 10;

  useEffect(() => {
    const fn = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', fn);
    return () => window.removeEventListener('resize', fn);
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    setModalOpen(false);
  }, [filters.property_id, filters.building_name]);

  const propertyContext = useMemo(() => {
    const prop = properties.find((p) => p._id === filters.property_id);
    return {
      propertyId: filters.property_id || '',
      buildingName: filters.building_name || '',
      name: prop?.title || prop?.name || '',
      type: filters.property_type || '',
    };
  }, [properties, filters.property_id, filters.building_name, filters.property_type]);

  const sorted = useMemo(() => [...items].sort((a, b) => b.year - a.year), [items]);
  const totalPages = Math.ceil(sorted.length / PER_PAGE);
  const paged = sorted.slice((currentPage - 1) * PER_PAGE, currentPage * PER_PAGE);
  const canEdit = userRole === 'company' || userRole === 'auditor' || (userRole === 'technical staff' && !!userLead);

  // "Copy to next year": offered once last year's schedule exists for this
  // building and the target year does not yet. December targets next year.
  const now = new Date();
  const renewTarget = now.getMonth() === 11 ? now.getFullYear() + 1 : now.getFullYear();
  const sameBuilding = (r) => (r.property?.buildingName || '').trim().toLowerCase() === (filters.building_name || '').trim().toLowerCase();
  const hasYear = (y) => sorted.some((r) => Number(r.year) === y && sameBuilding(r));
  const canRenew = canEdit && !!filters.property_id && !!filters.building_name && hasYear(renewTarget - 1) && !hasYear(renewTarget);

  const adjustWidth = (key, delta) =>
    setColWidths((prev) => {
      const next = { ...prev, [key]: Math.max(60, (prev[key] || FACTORY_WIDTHS[key] || 110) + delta) };
      saveWidths(next);
      return next;
    });

  const totals = useMemo(() => {
    if (!sorted.length) return null;
    return {
      totalRecords: sorted.length,
      totalItems: sorted.reduce((s, r) => s + getItemCount(r), 0),
      totalOverdue: sorted.reduce((s, r) => s + getOverdueCount(r), 0),
      totalCompleted: sorted.reduce((s, r) => s + getCompletedCount(r), 0),
      totalSkipped: sorted.reduce((s, r) => s + getSkippedCount(r), 0),
      totalReminders: sorted.reduce((s, r) => s + getReminderCount(r), 0),
    };
  }, [sorted]);

  const handleOpenNew = () => { setModalData(null); setIsNewModal(true); setModalOpen(true); };
  const handleOpenEdit = (row) => { setModalData(row); setIsNewModal(false); setModalOpen(true); };

  const handleModalSave = useCallback(async (payload, isNew) => {
    const result = await onSaveRow({ ...payload, _id: isNew ? undefined : modalData?._id });
    if (onRefresh) onRefresh();
    return result?.data || result;
  }, [onSaveRow, onRefresh, modalData]);

  const handleRenew = async () => {
    const source = sorted.find((r) => Number(r.year) === renewTarget - 1 && sameBuilding(r));
    const n = getItemCount(source || {});
    const res = await Swal.fire({
      title: 'Copy to ' + renewTarget + '?',
      html: '<div style="text-align:left;line-height:1.6">Copy <strong>' + n + ' item' + (n === 1 ? '' : 's') + '</strong> from ' + (renewTarget - 1) + ' into ' + renewTarget + '.<br/>Due dates keep their month and day. Completions, skips and files are not copied.</div>',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#1A6B4A',
      confirmButtonText: 'Copy',
      reverseButtons: true,
    });
    if (!res.isConfirmed) return;
    setRenewing(true);
    try {
      const out = await renewInspectionRecord({ propertyId: filters.property_id, buildingName: filters.building_name, targetYear: renewTarget });
      toast.success(out?.message || 'Copied to ' + renewTarget);
      onRefresh?.();
    } catch {
      // The Api layer has already toasted the server's message.
    } finally {
      setRenewing(false);
    }
  };

  const columns = [
    { key: 'property', label: 'Property / Building', sticky: true },
    { key: 'dueDate', label: 'Due Date' },
    { key: 'inspectionType', label: 'Type' },
    { key: 'frequency', label: 'Frequency' },
    { key: 'itemCount', label: 'Items' },
    { key: 'overdue', label: 'Overdue' },
    { key: 'attachments', label: 'Files' },
    { key: 'updated', label: 'Last Updated' },
    { key: 'actions', label: 'Actions' },
  ];

  const cw = (key) => colWidths[key] || FACTORY_WIDTHS[key] || 110;
  const maxH = windowWidth < scrollThreshold ? '62vh' : '72vh';

  const headerTitle = propertyContext.name
    ? `${propertyContext.name}${propertyContext.buildingName ? ` — ${propertyContext.buildingName}` : ''}${propertyContext.type ? ` (${propertyContext.type})` : ''}`
    : 'Select a Property to Begin';

  return (
    <Paper elevation={0} className="it-wrap it-paper">
      <div className="it-header">
        <div className="it-header__left">
          <div className="it-header__icon-wrap"><FeatherIcon icon="clipboard" size={18} color="#A7F3D0" /></div>
          <div className="it-header__meta">
            <div className="it-header__label">Inspection Tracker</div>
            <div className="it-header__title">{headerTitle}</div>
          </div>
          <div className="it-header__badges">
            {sorted.length > 0 && <span className="it-header__badge it-header__badge--records">{sorted.length} record{sorted.length !== 1 ? 's' : ''}</span>}
            {totals?.totalOverdue > 0 && <span className="it-header__badge it-header__badge--overdue">{totals.totalOverdue} overdue</span>}
            {totals?.totalCompleted > 0 && <span className="it-header__badge" style={{ background: '#E8F5EE', color: '#1A6B4A' }}>{totals.totalCompleted} completed</span>}
            {totals?.totalSkipped > 0 && <span className="it-header__badge" style={{ background: '#FEFCE8', color: '#A16207' }}>{totals.totalSkipped} skipped</span>}
            {totals?.totalReminders > 0 && <span className="it-header__badge" style={{ background: '#FFFBEB', color: '#D97706' }}>🔔 {totals.totalReminders} reminder{totals.totalReminders !== 1 ? 's' : ''}</span>}
          </div>
        </div>
        <div className="it-header__actions">
          <DueDateLegendTooltip />
          {canRenew && (
            <button type="button" className="it-btn it-btn--ghost-light" onClick={handleRenew} disabled={renewing} aria-busy={renewing}>
              {renewing ? <Spinner size="sm" /> : <FeatherIcon icon="copy" size={14} />} Copy to {renewTarget}
            </button>
          )}
          {canEdit && filters.property_id && filters.building_name && (
            <button type="button" className="it-btn it-btn--primary-solid it-add" onClick={handleOpenNew}>
              <FeatherIcon icon="plus" size={14} /> Add Inspection
            </button>
          )}
        </div>
      </div>

      <Box sx={{ maxHeight: maxH, overflowY: 'auto', overflowX: 'auto', borderRadius: '0 0 10px 10px' }} className="it-scroll-box">
        <table className="it-tbl">
          <thead>
            <tr>
              {columns.map((col) => {
                const w = cw(col.key);
                return (
                  <th key={col.key} className={col.sticky ? 'it-sticky' : ''} style={{ width: w, minWidth: w }}>
                    <div className="th-inner">
                      {col.key !== 'expand' && col.key !== 'actions' && col.label}
                      {col.key !== 'expand' && col.key !== 'actions' && <ResizeHandle onResize={(delta) => adjustWidth(col.key, delta)} />}
                    </div>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paged.length === 0 && (
              <tr>
                <td colSpan={columns.length} className="it-empty-cell">
                  <div className="it-empty">
                    <div className="it-empty__icon-wrap"><FeatherIcon icon="clipboard" size={26} color="#1A6B4A" /></div>
                    <Typography className="it-empty__title">No Inspection Records Found</Typography>
                    <Typography className="it-empty__sub">
                      {!filters.property_id ? 'Select a property above to view or add inspections.'
                        : !filters.building_name ? 'Select a building to continue.'
                          : canEdit ? 'Click "Add Inspection" to create one.' : 'No records match the current filters.'}
                    </Typography>
                  </div>
                </td>
              </tr>
            )}

            {paged.map((row) => (
              <tr
                key={row._id}
                className={`it-row${getOverdueCount(row) > 0 ? ' it-overdue' : ''}`}
                onClick={() => handleOpenEdit(row)}
              >
                {columns.map((col) => {
                  const w = cw(col.key);
                  if (col.key === 'expand') {
                    return (
                      <td key="exp" className="it-expand-td" style={{ width: w }}>
                        <div className="it-expand-icon it-expand-icon--view"><FeatherIcon icon="edit-3" size={12} color="#1A6B4A" /></div>
                      </td>
                    );
                  }
                  if (col.key === 'actions') {
                    return (
                      <td key="act" className="it-actions-td" style={{ width: w }} onClick={(e) => e.stopPropagation()}>
                        <div className="it-action-group">
                          <Tooltip title={canEdit ? 'View / Edit' : 'Open: record completions and attach photos'} arrow>
                            <button type="button" className="it-ebtn" onClick={(e) => { e.stopPropagation(); handleOpenEdit(row); }} aria-label="Open record">
                              <FeatherIcon icon={canEdit ? 'edit-2' : 'check-square'} size={13} color="#1A6B4A" />
                            </button>
                          </Tooltip>
                          {canEdit && (
                            <Tooltip title="Delete" arrow>
                              <button type="button" className="it-dbtn" onClick={(e) => { e.stopPropagation(); onDelete(row); }}>
                                <FeatherIcon icon="trash-2" size={13} color="#DC2626" />
                              </button>
                            </Tooltip>
                          )}
                        </div>
                      </td>
                    );
                  }
                  return (
                    <td key={col.key} className={col.sticky ? 'it-sticky' : ''} style={{ width: w }}>
                      <span className="it-cell-inner">{cellVal(row, col.key, propertyContext)}</span>
                    </td>
                  );
                })}
              </tr>
            ))}

            {totals && paged.length > 0 && (
              <tr className="it-summary">
                <td className="it-summary__label-cell">
                  <span className="it-summary__label-inner">
                    <FeatherIcon icon="list" size={12} color="#6B8F7C" />
                    {totals.totalRecords} Record{totals.totalRecords !== 1 ? 's' : ''}
                  </span>
                </td>
                <td className="it-summary__empty-cell">—</td>
                <td className="it-summary__empty-cell">—</td>
                <td className="it-summary__empty-cell">—</td>
                <td className="it-summary__label-cell">
                  {totals.totalItems} items
                  {totals.totalCompleted > 0 && <span style={{ fontSize: '0.68rem', color: '#1A6B4A', marginLeft: 4 }}>({totals.totalCompleted}✓)</span>}
                  {totals.totalReminders > 0 && <span style={{ fontSize: '0.68rem', color: '#D97706', marginLeft: 4 }}>🔔{totals.totalReminders}</span>}
                </td>
                <td className="it-summary__issues-cell">
                  <span className={`it-badge ${totals.totalOverdue > 0 ? 'it-badge--overdue' : 'it-badge--ok'}`}>{totals.totalOverdue} overdue</span>
                </td>
                <td className="it-summary__empty-cell">—</td>
                <td className="it-summary__empty-cell">—</td>
                <td className="it-summary__empty-cell">—</td>
              </tr>
            )}
          </tbody>
        </table>
      </Box>

      {totalPages > 1 && (
        <div className="it-pagination-bar">
          <Pagination size="sm" className="it-pg mb-0">
            <Pagination.Prev onClick={() => currentPage > 1 && setCurrentPage((p) => p - 1)} disabled={currentPage === 1} />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item key={i + 1} active={i + 1 === currentPage} onClick={() => setCurrentPage(i + 1)}>{i + 1}</Pagination.Item>
            ))}
            <Pagination.Next onClick={() => currentPage < totalPages && setCurrentPage((p) => p + 1)} disabled={currentPage === totalPages} />
          </Pagination>
        </div>
      )}

      <InspectionTrackerFormModal
        isOpen={modalOpen}
        onClose={(changed) => { setModalOpen(false); if (changed && onRefresh) onRefresh(); }}
        onSave={handleModalSave}
        inspectionData={isNewModal ? null : modalData}
        isNew={isNewModal}
        propertyContext={propertyContext}
        typeOptions={typeOptions}
        frequencyOptions={frequencyOptions}
        userRole={userRole}
        userLead={userLead}
      />
    </Paper>
  );
};

export { InspectionTrackerListTable };